/**
 * @file build-page-items.ts
 *
 * Pure utility for computing the visible page-number sequence in a pagination
 * bar, shared across tabs (Categories, Orders). Page 1 and the last page are
 * always shown; pages near the current one are shown and the gaps are collapsed
 * to `"..."`. Ported from the Website Builder v2 CategoriesTab utility; kept pure
 * so the ellipsis placement logic is unit-testable without mounting React.
 */

/** Number of page buttons shown on each side of the current page. */
const PAGINATION_WINDOW_SIZE = 2;

/**
 * Builds the ordered list of page numbers (and `"..."` ellipsis sentinels)
 * to display in the pagination bar.
 *
 * Page 1 and the last page are always included. Pages within
 * `PAGINATION_WINDOW_SIZE` of the current page are shown; gaps outside that
 * window are collapsed to `"..."`.
 *
 * @param page - The currently active page (1-based).
 * @param totalPages - Total number of pages.
 *
 * @returns An array of page numbers and `"..."` strings in display order.
 */
export function buildPageItems(page: number, totalPages: number): (number | '...')[] {
  const items: (number | '...')[] = [];

  items.push(1);

  if (page - PAGINATION_WINDOW_SIZE > 2) {
    items.push('...');
  }

  const start = Math.max(2, page - PAGINATION_WINDOW_SIZE);
  const end = Math.min(totalPages - 1, page + PAGINATION_WINDOW_SIZE);

  for (let i = start; i <= end; i++) {
    items.push(i);
  }

  if (page + PAGINATION_WINDOW_SIZE < totalPages - 1) {
    items.push('...');
  }

  if (totalPages > 1) {
    items.push(totalPages);
  }

  return items;
}
